import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react'
import { CellComponentProps } from 'react-spreadsheet'
import { Colors } from '~/assets/style/colors'
import { CellContainer } from '~/components/_styled/spreadsheet'
import { Icon } from '~/components/Icon'
import { Tooltip } from '~/components/Tooltip'

const getOffsetRect = (el: HTMLElement) => {
  return {
    width: el.offsetWidth,
    height: el.offsetHeight,
    left: el.offsetLeft,
    top: el.offsetTop,
  }
}

export const SpreadsheetCell: FunctionComponent<CellComponentProps<any> & { errorMessage?: string }> = ({
  column,
  row,
  selected,
  DataViewer: Dv,
  activate,
  active,
  data,
  dragging,
  evaluatedData,
  mode,
  select,
  setCellData,
  setCellDimensions,
  errorMessage,
}) => {
  const rootRef = useRef<HTMLTableCellElement | null>(null)
  const point = useMemo(
    (): { row: number; column: number } => ({
      row,
      column,
    }),
    [row, column]
  )
  const handleMouseDown = useCallback(
    (event: MouseEvent<HTMLTableCellElement>) => {
      if (mode === 'view') {
        setCellDimensions(point, getOffsetRect(event.currentTarget))

        if (event.shiftKey) {
          select(point)
        } else {
          activate(point)
        }
      }
    },
    [mode, setCellDimensions, point, select, activate]
  )

  const handleMouseOver = useCallback(
    (event: React.MouseEvent<HTMLTableCellElement>) => {
      if (dragging) {
        setCellDimensions(point, getOffsetRect(event.currentTarget))
        select(point)
      }
    },
    [setCellDimensions, select, dragging, point]
  )

  useEffect(() => {
    const root = rootRef.current
    if (selected && root) {
      setCellDimensions(point, getOffsetRect(root))
    }
    if (root && active && mode === 'view') {
      root.focus()
    }
  }, [setCellDimensions, selected, active, mode, point, data])

  return (
    <CellContainer
      readonly={!!data?.readOnly}
      hasError={!!errorMessage}
      onMouseOver={handleMouseOver}
      onMouseDown={handleMouseDown}
      tabIndex={0}
      className={cn('Spreadsheet__cell', data?.readOnly && 'Spreadsheet__cell--readonly')}
    >
      <div>
        <Dv row={row} column={column} cell={data} evaluatedCell={evaluatedData} setCellData={setCellData} />
        {errorMessage && (
          <Tooltip reposition title={errorMessage}>
            <span>{<Icon width={'16px'} fill={Colors.Alert} name={'warning'} />}</span>
          </Tooltip>
        )}
      </div>
    </CellContainer>
  )
}
